import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Divider, Popover, Stack, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useAppContext } from "../../../context/AppSettingsContextProvider";
import InputField from "../../../components/InputFields/InputField";
import { StyledBadge } from "./StyledBadge";
import { PlatformType, PlatformTemplate, ExternalPlatformMessagePayload } from "../../../interfaces/IExternalCommunication";
import { PlatformMessageProps } from "./ExternalCommunicationContainer";
import { EventDto } from "../../../interfaces/IEvent";
import EditableFields from "../../../components/InputFields/EditableFields";
import PlatformTemplateSelectField from "./PlatformTemplateSelectField";
import {getEstimatedDownTimeText, toDateTime} from "../../../utils/ValueFormatters";
import { EditableFieldsDate, EditableFieldsString } from "../../../interfaces/IFields";

interface ExternalCommunicationProps {
    viewEvent: EventDto;
    messageProps: PlatformMessageProps;
    templates: PlatformTemplate[];
    onChange: (platformMessage: ExternalPlatformMessagePayload) => void;
}

export default function ExternalCommunication({ viewEvent, templates, messageProps, onChange }: ExternalCommunicationProps){
    const { isDarkTheme } = useAppContext();
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const [background, setBackground] = useState("");
    const [color, setColor] = useState("");
    const theme = useTheme();
    const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
    const [currentTemplate, setCurrentTemplate] = useState<PlatformTemplate>({} as PlatformTemplate);
    const [externalPlatformMessagePayload, setExternalPlatformMessagePayload] = useState<ExternalPlatformMessagePayload>({
        message: messageProps.platformMessage.template!,
        messageIsExternallyVisible: messageProps.messageIsExternallyVisible
    } as ExternalPlatformMessagePayload);

    const [shouldSetInitialEditableFields, setShouldInitialEditableFields] = useState(false);
    const [changeTemplateText, setChangeTemplateText] = useState(false);

    const activatedBackgroundColor = theme.palette.status.active;
    const activatedColorColor = theme.palette.status.active;
    const activatedButNoAffectedCustomersBackgroundColor = theme.palette.status.noAffected;
    const activatedButNoAffectedCustomersColorColor = theme.palette.status.noAffected;
    const deactivatedBackgroundColor = theme.palette.status.deactivated;
    const deactivatedColorColor = theme.palette.status.deactivated;

    const handleTemplateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedTemplate = templates.find((template) => template.id === Number(event.target.value))!;
        setCurrentTemplate(selectedTemplate);
        setExternalPlatformMessagePayload({
            ...externalPlatformMessagePayload,
            message: {
                ...externalPlatformMessagePayload.message,
                id: selectedTemplate.id,
                text: selectedTemplate.text || "",
                editedText: selectedTemplate.text || "",
                subject: selectedTemplate.subject || "",
                editedSubject: selectedTemplate.subject || "",
                editableFields: selectedTemplate.editableFields
            }
        });
        setShouldInitialEditableFields(true);
    }

    const handleVisible = () => {
        setExternalPlatformMessagePayload({
            ...externalPlatformMessagePayload,
            messageIsExternallyVisible: !externalPlatformMessagePayload.messageIsExternallyVisible
        });
    }

    const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternalPlatformMessagePayload({
            ...externalPlatformMessagePayload,
            message: {
                ...externalPlatformMessagePayload.message,
                subject: event.target.value,
                editedSubject: event.target.value
            }
        });

        setChangeTemplateText(true);
    }

    const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternalPlatformMessagePayload({
            ...externalPlatformMessagePayload,
            message: {
                ...externalPlatformMessagePayload.message,
                text: event.target.value,
                editedText: event.target.value
            }
        });

        setChangeTemplateText(true);
    }

    const handleEditableFieldsChange = (name: string, value: Date | string) => {
        setExternalPlatformMessagePayload((prevState) => ({
            ...prevState,
            message: {
                ...prevState.message,
                editableFields: {
                    ...prevState.message.editableFields,
                    [name]: value
                }
            }
        }));

        setChangeTemplateText(true);
    }

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSubmit = () => {
        onChange(externalPlatformMessagePayload);
        setAnchorEl(null);
    }

    const handleClose = () => {
        setExternalPlatformMessagePayload({
            ...externalPlatformMessagePayload,
            message: messageProps.platformMessage.template!
        });
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    useEffect(() => {
        if (templates.length > 0 && externalPlatformMessagePayload.message?.id) {
            const selectedTemplate = templates.find(template => template.id === externalPlatformMessagePayload.message.id);
            if (selectedTemplate) {
                setCurrentTemplate(selectedTemplate);
            }
        }
    }, [templates, externalPlatformMessagePayload.message?.id]);

    useEffect(() => {
        if(changeTemplateText){
            let {text, subject, editableFields} = externalPlatformMessagePayload.message

            if(editableFields){
                Object.keys(editableFields).forEach((editableFieldKey) => {
                    let editableField = editableFields[editableFieldKey] as Date | string;

                    if (Object.values(EditableFieldsString).includes(editableFieldKey as EditableFieldsString) && editableField !== "" && editableField !== null) {
                        if (editableFieldKey === EditableFieldsString.downTime) {
                            editableField = getEstimatedDownTimeText(editableField as string);
                        }

                        text = text.replaceAll(`${editableFieldKey}`, editableField as string);
                        if (subject && subject.includes(`${editableFieldKey}`)) {
                            subject = subject.replaceAll(`${editableFieldKey}`, editableField as string);
                        }
                    }
                    else if (Object.values(EditableFieldsDate).includes(editableFieldKey as EditableFieldsDate) && editableField !== "" && editableField !== null) {
                        const value = toDateTime(new Date(editableField));
                        text = text.replaceAll(`${editableFieldKey}`, value);
                        if (subject && subject.includes(`${editableFieldKey}`)) {
                            subject = subject.replaceAll(`${editableFieldKey}`, value);
                        }
                    }
                    else {
                        text = text.replaceAll(`${editableFieldKey}`, "ukjent");
                        if(subject && subject.includes(`${editableFieldKey}`)) {
                            subject = subject.replaceAll(`${editableFieldKey}`, "ukjent");
                        }
                    }
                });
            }
            
            setExternalPlatformMessagePayload((prevState) => ({
                ...prevState,
                message: {
                    ...prevState.message,
                    editedText: text,
                    editedSubject: subject
                }
            }));

        }

        setChangeTemplateText(false);
    }, [changeTemplateText]);

    useEffect(() => {
        if(shouldSetInitialEditableFields){
            if(EditableFieldsDate.startTimeDate in externalPlatformMessagePayload.message.editableFields 
                && viewEvent.eventDetails.fromDate !== null){
                    handleEditableFieldsChange(EditableFieldsDate.startTimeDate, viewEvent.eventDetails.fromDate);
            }
            if(EditableFieldsDate.endTimeDate in externalPlatformMessagePayload.message.editableFields
                && viewEvent.eventDetails.toDate !== null){
                    handleEditableFieldsChange(EditableFieldsDate.endTimeDate, viewEvent.eventDetails.toDate);
            }
            if(EditableFieldsString.downTime in externalPlatformMessagePayload.message.editableFields
                && viewEvent.eventDetails.estimatedDowntime !== null){
                    handleEditableFieldsChange(EditableFieldsString.downTime, viewEvent.eventDetails.estimatedDowntime);
            }

            setChangeTemplateText(true);
        }

        setShouldInitialEditableFields(false);
    }, [viewEvent, shouldSetInitialEditableFields])

    useEffect(() => {
        if(messageProps.messageIsExternallyVisible){
            if((!viewEvent.affectedCustomers || viewEvent.affectedCustomers.length === 0) && !viewEvent.isAllCompanyCustomersAffected && messageProps.platformType !== PlatformType.EidsivaNo){
                setBackground(activatedButNoAffectedCustomersBackgroundColor);
                setColor(activatedButNoAffectedCustomersColorColor);
                return;
            }

            setBackground(activatedBackgroundColor);
            setColor(activatedColorColor);
        } else {
            setBackground(deactivatedBackgroundColor);
            setColor(deactivatedColorColor);
        }
    }, [messageProps.messageIsExternallyVisible, viewEvent.isAllCompanyCustomersAffected, viewEvent.affectedCustomers?.length, messageProps.platformType]);

    return (
        <StyledBadge
            custombackgroundcolor={background}
            customcolor={color}
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
        >
            <Avatar
                onClick={(e) => handleClick(e)}
                title={messageProps.headerText?.toString()}
                sx={{
                    cursor: "pointer",
                    height: isMdDown ? "40px" : "55px",
                    width: isMdDown ? "40px" : "55px",
                    backgroundColor: "background.paper",
                    '&:hover': {
                        backgroundColor: "background.default",
                    },
                    border: `2px solid ${background}}`
                }}
                alt="Eidsiva"
            >
                {isDarkTheme ? messageProps.iconWhite : messageProps.icon}
            </Avatar>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                sx={{
                    width: isMdDown ? "auto" : "2000px"
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        width: isMdDown ? "auto" : "500px"
                    }}>
                    <Stack direction={"column"} spacing={2}>
                        {messageProps.headerText}
                        <Divider variant="fullWidth"/>
                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                alignSelf: "left"
                            }}>
                            <Switch 
                                size="medium" 
                                checked={externalPlatformMessagePayload.messageIsExternallyVisible} 
                                color={externalPlatformMessagePayload?.messageIsExternallyVisible
                                    ? (!viewEvent.affectedCustomers || viewEvent.affectedCustomers.length === 0) && !viewEvent.isAllCompanyCustomersAffected && messageProps.platformType !== PlatformType.EidsivaNo
                                        ? "warning"
                                        : "success"
                                    : "error"}
                                onClick={handleVisible}
                            />
                            <Typography 
                                variant="body2" 
                                color={externalPlatformMessagePayload.messageIsExternallyVisible
                                    ? (!viewEvent.affectedCustomers || viewEvent.affectedCustomers.length === 0) && !viewEvent.isAllCompanyCustomersAffected && messageProps.platformType !== PlatformType.EidsivaNo
                                        ? activatedButNoAffectedCustomersColorColor
                                        : activatedColorColor
                                    : deactivatedColorColor}
                                sx={{
                                    alignSelf: "center"
                                }}
                            >
                                {externalPlatformMessagePayload.messageIsExternallyVisible
                                    ? (!viewEvent.affectedCustomers || viewEvent.affectedCustomers.length === 0) && !viewEvent.isAllCompanyCustomersAffected && messageProps.platformType !== PlatformType.EidsivaNo
                                        ? "Aktivert (ingen berørte)"
                                        : "Aktivert"
                                    : "Deaktivert"}
                            </Typography>
                        </Stack>
                        {currentTemplate && 
                            <PlatformTemplateSelectField
                                title="Mal"
                                name="template"
                                onChange={handleTemplateChange}
                                value={currentTemplate.id}
                                templates={templates.filter((template) => template.platformType === currentTemplate.platformType)}
                            />
                        }
                        <InputField
                            title="Tittel"
                            name={externalPlatformMessagePayload.message.subject || ""}
                            placeholder="Skriv inn tittel"
                            onChange={handleSubjectChange}
                            value={externalPlatformMessagePayload.message.subject}
                            multiline
                            rows={2}
                            maxLength={80}
                        />
                        <EditableFields 
                            editableFields={externalPlatformMessagePayload.message.editableFields}
                            onReplaceContentText={handleEditableFieldsChange}
                        />
                        <Box sx={{
                            width: "100%"
                        }}>
                            <InputField
                                title="Melding"
                                name={externalPlatformMessagePayload.message.text || ""}
                                placeholder="Skriv inn melding"
                                onChange={handleMessageChange}
                                value={externalPlatformMessagePayload.message.text}
                                multiline
                                rows={10}
                                maxLength={255}
                            />
                        </Box>
                        <Stack direction={"row"} spacing={"auto"} >
                            <Button variant="contained" color="error" onClick={handleClose}>
                                Avbryt
                            </Button>
                            <Button variant="contained" color="primary" type="submit" onClick={handleSubmit}>
                                Lagre
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Popover>
        </StyledBadge>
    );
}